import React from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  AutocompleteProps as BaseMuiAutocompleteProps,
  FilterOptionsState,
  Autocomplete as MUIAutocomplete,
  Popper,
  TextField,
  useTheme,
  AutocompleteValue as Value,
} from '@mui/material';
import type { PopperProps } from '@mui/material/Popper';

import clsx from 'clsx';
import mergeWith from 'lodash/mergeWith';

import mixinSx from '@watershed/style/mixinSx';

import usePaletteUtils from '../../hooks/usePaletteUtils';
import { AutocompletePaper } from './AutocompletePaper';
import { ValidationState } from './Field';
import type { MuiTextFieldProps } from './TextField';
import {
  AutocompleteFilterConfig,
  useAutocompleteFilter,
} from './useAutocompleteFilter';

/** Default max number of results to return from the filter */
const DEFAULT_RESULT_LIMIT = 1000;

/**
 * These exclude deprecated props. Use slots/slotProps instead for those:
 * https://mui.com/material-ui/migration/migrating-from-deprecated-apis/#autocomplete
 */
export type MuiAutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisabledClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Omit<
  BaseMuiAutocompleteProps<T, Multiple, DisabledClearable, FreeSolo>,
  | 'PaperComponent'
  | 'PopperComponent'
  | 'ListboxComponent'
  | 'ChipProps'
  | 'ListboxProps'
  | 'components'
  | 'componentsProps'
>;

/**
 * These type Watershed's AutocompleteProps, for use with this component.
 */
export type AutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisabledClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Omit<
  MuiAutocompleteProps<T, Multiple, DisabledClearable, FreeSolo>,
  'renderInput' | 'components' | 'componentsProps' | 'filterOptions'
> & {
  variant?: MuiTextFieldProps['variant'];
  fsUnmask?: boolean;
  placeholder?: MuiTextFieldProps['placeholder'];
  validationState?: ValidationState;
  validationMessage?: React.ReactNode;
  autocompleteOff?: boolean;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  textFieldSx?: MuiTextFieldProps['sx'];
  textFieldSlotProps?: MuiTextFieldProps['slotProps'];
  /**
   * A custom function to use to filter options for the autocomplete. In general, using
   * the built-in optimized function via filterOptionsConfig is preferred, particularly for large lists.
   */
  filterOptions?: (options: Array<T>, state: FilterOptionsState<T>) => Array<T>;
  /**
   * The filter config to use for the optimized filterOptions function. If a custom
   * filterOptions prop is provided, this will be ignored.
   */
  filterOptionsConfig?: Partial<AutocompleteFilterConfig<T>>;
};

const FONT_SIZE_SMALL = 13;
const FONT_SIZE_LARGE = 14;
const SVG_SIZE_SMALL = 12;
const SVG_SIZE_LARGE = 16;
const SVG_RIGHT_PADDING_SMALL = 5;
const SVG_RIGHT_PADDING_LARGE = 9;

export function GrowingSelectPopper(props: PopperProps) {
  return (
    <Popper
      sx={{
        width: 'fit-content !important',
        // the popper receives the width of the autocomplete as style.width, this let's us keep that as a minimum but grow with content
        minWidth: props.style?.width,
        maxWidth: '800px',
      }}
      {...props}
    />
  );
}

export function FixedWidthSelectPopper(props: PopperProps) {
  return <Popper {...props} style={{ width: '350px' }} />;
}

// Default getOptionLabel implementation - typesafe implementation of
// https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Autocomplete/Autocomplete.js#L519
function defaultGetOptionLabel(option: unknown): string {
  return typeof option === 'string'
    ? option
    : option &&
        typeof option === 'object' &&
        'label' in option &&
        typeof option.label === 'string'
      ? option.label
      : '';
}

export function Autocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisabledClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(props: AutocompleteProps<T, Multiple, DisabledClearable, FreeSolo>) {
  const {
    validationState,
    validationMessage,
    // Disable browser's native autocomplete by default to prevent it from overlapping with our dropdown.
    // Only enable it for form fields like username/email where browser autocomplete is helpful.
    autocompleteOff = true,
    fsUnmask,
    textFieldSx: textFieldSxInitial,
    textFieldSlotProps,
    variant,
    filterOptionsConfig: filterOptionsConfigProp,
    ...autocompleteProps
  } = props;
  const isErrorState = validationState === 'error';
  const theme = useTheme();
  const paletteUtils = usePaletteUtils();

  const getOptionLabel = props.getOptionLabel ?? defaultGetOptionLabel;

  const filterOptionsConfig = {
    ...filterOptionsConfigProp,
    limit: filterOptionsConfigProp?.limit ?? DEFAULT_RESULT_LIMIT,
    stringify: filterOptionsConfigProp?.stringify ?? getOptionLabel,
  };
  const optimizedFilterOptions = useAutocompleteFilter(
    // We can't conditionally call the hook, and the hook does some potentially
    // expensive setup on options to run the optimized filter. So if filterOptions
    // was provided and we're going to ignore the hook's output anyway, pass an empty
    // options array to avoid doing a bunch of unnecessary work.
    props.filterOptions ? [] : props.options,
    filterOptionsConfig
  );
  const filterOptions = props.filterOptions ?? optimizedFilterOptions;

  // Reduce the size of options when size is "small".
  const slotProps =
    props.size === 'small'
      ? {
          paper: {
            sx: {
              fontSize: FONT_SIZE_SMALL,
              '& .MuiAutocomplete-listbox': { paddingX: 0, paddingY: 0.5 },
              // Specificity boost.
              '& .MuiAutocomplete-option.MuiAutocomplete-option': {
                paddingX: 1,
                paddingY: 0.25,
              },
            },
          },
        }
      : props.slotProps;

  let inputPadding: string;
  if (props.size === 'small') {
    if (props.disableClearable) {
      inputPadding = `0 ${
        SVG_SIZE_SMALL + SVG_RIGHT_PADDING_SMALL + 4
      }px 0 8px`;
    } else {
      inputPadding = `0 ${
        SVG_SIZE_SMALL * 2 + SVG_RIGHT_PADDING_SMALL + 4
      }px 0 8px`;
    }
  } else {
    if (props.disableClearable) {
      inputPadding = `4px ${
        SVG_SIZE_LARGE + SVG_RIGHT_PADDING_LARGE + 4
      }px 4px 12px`;
    } else {
      inputPadding = `4px ${
        SVG_SIZE_LARGE * 2 + SVG_RIGHT_PADDING_LARGE + 4
      }px 4px 12px`;
    }
  }

  const textFieldSx = mixinSx(
    {
      '& input::placeholder': paletteUtils.inputPlaceholderStyles,
      padding: 0,
      // Specificity boost.
      '& .MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment':
        props.size === 'small'
          ? {
              '& svg': { height: SVG_SIZE_SMALL, width: SVG_SIZE_SMALL },
              right: SVG_RIGHT_PADDING_SMALL,
              top: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }
          : {},
      '& .MuiAutocomplete-clearIndicator': { padding: '2px' },
    },
    textFieldSxInitial
  );

  const inputPropsStyle = {
    fontSize: props.size === 'small' ? FONT_SIZE_SMALL : FONT_SIZE_LARGE,
    padding: inputPadding,
    backgroundColor: theme.palette.background.paper,
    ...(isErrorState ? { boxShadow: paletteUtils.boxShadowField.error } : {}),
    ...(validationState === 'warning'
      ? { boxShadow: paletteUtils.boxShadowField.warning }
      : {}),
  };

  return (
    <MUIAutocomplete
      {...autocompleteProps}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      size="small"
      fullWidth
      slotProps={slotProps}
      renderInput={(params) => {
        // start adornments on InputProps have to be deep combined here since the default chips are start adornments
        const InputProps = mergeWith(
          {},
          params.InputProps,
          textFieldSlotProps?.input,
          {
            style: inputPropsStyle,
          },
          (objValue, srcValue, key) => {
            if (key === 'className') return clsx(objValue, srcValue);
            if (key === 'startAdornment') {
              // The order is backwards but needs to be that way so the chips are after whatever the user provided start adornments are
              return (
                <>
                  {srcValue}
                  {objValue}
                </>
              );
            } else if (key === 'endAdornment') {
              return (
                <>
                  {objValue}
                  {srcValue}
                </>
              );
            }
            // fall back otherwise to regular merge
          }
        );
        return (
          <TextField
            {...params}
            sx={textFieldSx}
            autoFocus={props.autoFocus ?? false}
            variant={variant ?? 'outlined'}
            placeholder={props.placeholder}
            error={isErrorState}
            helperText={isErrorState ? validationMessage : null}
            slotProps={{
              input: InputProps,

              htmlInput: {
                ...params.inputProps,
                className: clsx(
                  params.inputProps.className,
                  fsUnmask && 'fs-unmask'
                ),
                autoComplete: autocompleteOff && 'off',
                'aria-autocomplete': props['aria-autocomplete'],
              },
            }}
          />
        );
      }}
      onClose={(evt, reason) => {
        if (reason === 'blur') {
          if (!evt || !props.onChange) {
            // This can happen if the user enters a search term that doesn't
            // match any of the happens, then hits <enter>.
            return;
          }
          const value = (evt.target as HTMLInputElement).value;

          if (props.freeSolo) {
            props.onChange(
              evt,
              value as Value<T, Multiple, DisabledClearable, FreeSolo>,
              reason
            );
          } else if (props.value || props.disableClearable) {
            // If the autcomplete is not clearable or a value is selected, check whether
            // there is a single option that matches the input. If only one option exists, and
            // it doesn't match the selected value, select it.
            const possibleOptions = filterOptions(
              // The cast here shouldn't be necessary, because filterOptions should accept a readonly array,
              // but the MUI types don't allow it
              props.options as Array<T>,
              {
                inputValue: value,
                getOptionLabel,
              }
            );

            if (
              possibleOptions.length === 1 &&
              possibleOptions[0] !== props.value
            ) {
              props.onChange(
                evt,
                (autocompleteProps.multiple
                  ? possibleOptions
                  : possibleOptions[0]) as Value<
                  T,
                  Multiple,
                  DisabledClearable,
                  FreeSolo
                >,
                reason
              );
            }
          }
        }
      }}
      slots={{
        ...props.slots,
        paper:
          // Use a custom paper slot if the caller provided one
          props.slots?.paper ??
          // Otherwise, use our custom paper, which shows a message if the options are truncated.
          // Skip if a custom filterOptions function is provided - we won't be using our optimized filter, so the limit logic doesn't apply
          (props.filterOptions ||
          // Skip if the number of options is less than the limit - we won't ever need the message
          props.options.length < filterOptionsConfig.limit
            ? undefined
            : AutocompletePaper({ maxItems: filterOptionsConfig.limit })),
      }}
      openOnFocus={props.openOnFocus ?? true}
      autoHighlight={props.autoHighlight ?? true}
    />
  );
}
